class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.close = this.close.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlayClose();
    this.bindExplicitCloseButtons();
    this.bindCartIcon();

    const initialCount = Number(this.querySelector('[data-cart-drawer-count]')?.dataset.count || 0);
    if (initialCount > 0) {
      this._displayCount = initialCount;
      this.hideEmptyState();
      this.enableCheckoutButtons();
      this.updateDrawerCount(initialCount, { animate: false });
      window.BeanyCartDrawerPricing?.applyInventory?.(initialCount);
    } else if (this.classList.contains('is-empty')) {
      this.dataset.beanyCartHasItems = 'false';
      const warmPrefetch = () => this.prefetchSections();
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(warmPrefetch, { timeout: 2000 });
      } else {
        window.setTimeout(warmPrefetch, 800);
      }
    }
  }

  bindOverlayClose() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.beanyCloseBound === 'true') return;
    overlay.dataset.beanyCloseBound = 'true';
    overlay.addEventListener('click', (event) => this.close(event));
  }

  bindExplicitCloseButtons() {
    if (this.dataset.beanyExplicitCloseBound === 'true') return;
    this.dataset.beanyExplicitCloseBound = 'true';
    this.addEventListener('click', (event) => {
      const btn = event.target?.closest?.(
        '[data-beany-empty-close], .drawer__close, .beany-cart-drawer__close'
      );
      if (!btn || !this.contains(btn)) return;
      event.preventDefault();
      event.stopPropagation();
      // Button closes must never be blocked by the overlay open-grace window.
      this._ignoreOverlayCloseUntil = 0;
      this.close();
    });
  }

  bindCartIcon() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.drawerBound === 'true') return;

    cartLink.dataset.drawerBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    // Prefetch only on press — do not open yet (avoids overlay click-through closing the drawer).
    cartLink.addEventListener('pointerdown', () => {
      this.prefetchSections();
      if (this.getResolvedBagCount() > 0) {
        this.syncDrawerUiOnOpen();
      }
    }, { passive: true });

    cartLink.addEventListener('mouseenter', () => this.prefetchSections(), { passive: true });
    cartLink.addEventListener('focus', () => this.prefetchSections());

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.openFromIcon(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE' || event.code.toUpperCase() === 'ENTER') {
        event.preventDefault();
        this.openFromIcon(cartLink);
      }
    });
  }

  getResolvedBagCount() {
    if (typeof this._displayCount === 'number' && this._displayCount > 0) {
      return this._displayCount;
    }

    let total = 0;
    this.querySelectorAll('.beany-cart-drawer-qty .quantity__input').forEach((input) => {
      total += Number.parseInt(input.value, 10) || 0;
    });
    if (total > 0) return total;

    const stored = Number(
      this.dataset.beanyCount ||
        this.querySelector('[data-cart-drawer-count]')?.dataset.count ||
        0
    );
    if (stored > 0) return stored;

    const headerCount = Number.parseInt(
      document.querySelector('#cart-icon-bubble .cart-count-bubble [aria-hidden="true"]')?.textContent?.trim() || '',
      10
    );
    if (headerCount > 0) return headerCount;

    return 0;
  }

  syncDrawerUiOnOpen() {
    const bagCount = this.getResolvedBagCount();
    if (bagCount < 1) return;

    this.hideEmptyState();
    this.updateDrawerCount(bagCount, { animate: false });
    window.BeanyCartDrawerPricing?.applyInventory?.(bagCount);
    window.BeanyCartDrawerPricing?.apply?.(bagCount);
  }

  showEmptyState() {
    this.dataset.beanyCartHasItems = 'false';
    this.classList.add('is-empty');
    this.querySelector('cart-drawer-items')?.classList.add('is-empty');
    this.querySelector('.drawer__inner-empty')?.removeAttribute('hidden');
    this.querySelector('.beany-cart-drawer__filled')?.setAttribute('hidden', '');
    this.querySelector('.drawer__footer')?.setAttribute('hidden', '');
    this.querySelector('[data-beany-cart-inventory-message]')?.setAttribute('hidden', '');
    const emptyClose = this.querySelector('[data-beany-empty-close]');
    if (emptyClose) {
      emptyClose.removeAttribute('hidden');
      emptyClose.style.removeProperty('display');
      emptyClose.style.setProperty('position', 'absolute', 'important');
      emptyClose.style.setProperty('top', '12px', 'important');
      emptyClose.style.setProperty('left', '12px', 'important');
      emptyClose.style.setProperty('right', 'auto', 'important');
      emptyClose.style.setProperty('bottom', 'auto', 'important');
      emptyClose.style.setProperty('margin', '0', 'important');
      emptyClose.style.setProperty('transform', 'none', 'important');
      emptyClose.style.setProperty('z-index', '999', 'important');
    }
    this.querySelector('.drawer__header')?.setAttribute('hidden', '');
  }

  hideEmptyState() {
    this.dataset.beanyCartHasItems = 'true';
    this.classList.remove('is-empty');
    this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    this.querySelector('.drawer__inner-empty')?.setAttribute('hidden', '');
    this.querySelector('.beany-cart-drawer__filled')?.removeAttribute('hidden');
    this.querySelector('.drawer__footer')?.removeAttribute('hidden');
    this.querySelector('.drawer__header')?.removeAttribute('hidden');
    this.querySelector('[data-beany-cart-inventory-message]')?.removeAttribute('hidden');
    const emptyClose = this.querySelector('[data-beany-empty-close]');
    if (emptyClose) {
      emptyClose.setAttribute('hidden', '');
      emptyClose.style.setProperty('display', 'none', 'important');
    }
  }

  enableCheckoutButtons() {
    this.querySelectorAll('#CartDrawer-Checkout, #CartDrawer-Subscribe, #CartDrawer-Checkout-OneTime, [data-beany-club-checkout]').forEach((btn) => {
      btn.removeAttribute('disabled');
    });
  }

  openEmptyDrawerFast(triggeredBy) {
    this.showEmptyState();
    this.updateDrawerCount(0, { animate: false });
    if (!this.classList.contains('active')) {
      this.open(triggeredBy, { immediate: true });
    }
    this.verifyEmptyCartInBackground(triggeredBy);
  }

  peekPrefetchedCart(maxAgeMs = 45000) {
    if (!this._prefetchedCart || Date.now() - (this._prefetchedAt || 0) > maxAgeMs) {
      return null;
    }
    return this._prefetchedCart;
  }

  verifyEmptyCartInBackground(triggeredBy) {
    const applyCart = (cart) => {
      if ((cart?.item_count ?? 0) > 0) {
        this.refreshFromCart({ open: true, triggeredBy });
      }
    };

    const prefetched = this.peekPrefetchedCart();
    if (prefetched) {
      applyCart(prefetched);
      return;
    }

    if (this._prefetchPromise) {
      void this._prefetchPromise.then(() => {
        applyCart(this.peekPrefetchedCart());
      });
      return;
    }

    const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
    fetch(cartUrl, { cache: 'no-store' })
      .then((response) => response.json())
      .then(applyCart)
      .catch(() => {});
  }

  openFromIcon(triggeredBy) {
    const bagCount = this.getResolvedBagCount();
    if (bagCount > 0) {
      this.syncDrawerUiOnOpen();
      this.open(triggeredBy, { immediate: true });
      return;
    }

    this.openEmptyDrawerFast(triggeredBy);
  }

  open(triggeredBy, options = {}) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Ignore the same tap/click that opened the drawer (overlay appears under the pointer).
    this._ignoreOverlayCloseUntil = Date.now() + 450;

    if (!this.classList.contains('active')) {
      const activate = () => {
        this.classList.add('animate', 'active');
      };
      if (options.immediate) {
        activate();
      } else {
        requestAnimationFrame(activate);
      }

      this.addEventListener('transitionend', () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      }, { once: true });
    }

    document.body.classList.add('overflow-hidden');
  }

  close(event) {
    // Only ignore accidental overlay taps from the same gesture that opened the drawer.
    // Explicit close controls (X, Escape, empty CTA) must always work.
    if (event && this._ignoreOverlayCloseUntil && Date.now() < this._ignoreOverlayCloseUntil) {
      const target = event.target;
      const isOverlay =
        target &&
        (target.id === 'CartDrawer-Overlay' || target.closest?.('#CartDrawer-Overlay'));
      const isExplicitClose = target?.closest?.(
        '[data-beany-empty-close], .drawer__close, .beany-cart-drawer__close'
      );
      if (isOverlay && !isExplicitClose) {
        return;
      }
    }

    this._ignoreOverlayCloseUntil = 0;
    this.classList.remove('active');
    // Belt-and-suspenders: closed drawer must not leave a dim layer.
    this.style.removeProperty('background-color');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  ensureCountBubble() {
    const titleRow = this.querySelector('.beany-cart-drawer__title-row');
    if (!titleRow) return null;

    let bubble = this.querySelector('[data-cart-drawer-count-bubble]');
    if (bubble) return bubble;

    bubble = document.createElement('div');
    bubble.className = 'cart-count-bubble beany-cart-drawer__count-bubble';
    bubble.setAttribute('data-cart-drawer-count-bubble', '');
    bubble.innerHTML =
      '<span aria-hidden="true" data-cart-drawer-count></span><span class="visually-hidden" data-cart-drawer-count-a11y></span>';
    titleRow.appendChild(bubble);
    return bubble;
  }

  syncHeaderIconCount(itemCount) {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    let bubble = cartLink.querySelector('.cart-count-bubble');
    if (itemCount > 0) {
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
        cartLink.appendChild(bubble);
      }
      const countSpan = bubble.querySelector('[aria-hidden="true"]');
      if (countSpan) countSpan.textContent = itemCount < 100 ? String(itemCount) : '';
      bubble.hidden = false;
      bubble.style.display = '';
    } else if (bubble) {
      bubble.hidden = true;
      bubble.style.display = 'none';
    }
  }

  updateDrawerCount(itemCount, options = {}) {
    const count = Math.max(0, Number(itemCount) || 0);
    this._displayCount = count;
    this.dataset.beanyCount = String(count);

    const bubble = this.ensureCountBubble();
    const countEl = this.querySelector('[data-cart-drawer-count]');
    const a11yEl = this.querySelector('[data-cart-drawer-count-a11y]');
    if (!bubble || !countEl) return;

    const previous = Number(countEl.dataset.count || 0);
    const shouldPop = options.animate !== false && count > previous;

    if (count > 0 && count < 100) {
      countEl.textContent = String(count);
      countEl.dataset.count = String(count);
      bubble.dataset.count = String(count);
      bubble.classList.add('is-live');
      bubble.removeAttribute('hidden');
      if (a11yEl) a11yEl.textContent = `עגלה, ${count} פריטים`;
      if (shouldPop) {
        countEl.classList.remove('is-pop');
        void countEl.offsetWidth;
        countEl.classList.add('is-pop');
      }
    } else if (count >= 100) {
      countEl.textContent = '';
      countEl.dataset.count = String(count);
      bubble.dataset.count = String(count);
      bubble.classList.add('is-live');
      bubble.removeAttribute('hidden');
    } else {
      countEl.textContent = '';
      countEl.dataset.count = '0';
      bubble.dataset.count = '0';
      bubble.classList.remove('is-live');
      bubble.setAttribute('hidden', '');
    }

    this.syncHeaderIconCount(count);
  }

  countDomBagTotal() {
    let total = 0;
    this.querySelectorAll('.beany-cart-drawer-qty .quantity__input').forEach((input) => {
      if (input.closest('[data-beany-gift]')) return;
      total += Number.parseInt(input.value, 10) || 0;
    });
    return total;
  }

  hasVisibleCartRows() {
    return this.querySelectorAll('#CartDrawer-CartItems .cart-item').length > 0;
  }

  resolveItemCount(parsedState) {
    if (Array.isArray(parsedState?.items) && parsedState.items.length > 0) {
      const fromItems = parsedState.items.reduce(
        (sum, item) => sum + (Number(item.quantity) || 0),
        0
      );
      if (fromItems > 0) return fromItems;
    }

    if (parsedState?.item_count != null && parsedState.item_count !== '') {
      const count = Number(parsedState.item_count);
      if (!Number.isNaN(count)) return count;
    }

    if (parsedState?.quantity != null) {
      const count = Number(parsedState.quantity);
      if (!Number.isNaN(count)) return count;
    }

    return 0;
  }

  async fetchCartJson() {
    const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
    try {
      const response = await fetch(cartUrl, { cache: 'no-store' });
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  }

  sectionHtmlHasCartItems(sectionHtml) {
    if (!sectionHtml) return false;
    return /class=["']cart-item["']/.test(sectionHtml);
  }

  shouldApplyDrawerSectionHtml(sectionHtml, itemCount) {
    if (!sectionHtml) return false;
    if (itemCount < 1) return true;
    return this.sectionHtmlHasCartItems(sectionHtml);
  }

  replaceDynamicSection(sectionHtml, itemCount) {
    if (!this.shouldApplyDrawerSectionHtml(sectionHtml, itemCount)) return false;

    const sectionElement =
      document.querySelector('#CartDrawer [data-beany-cart-drawer-dynamic]') ||
      document.querySelector('[data-beany-cart-drawer-dynamic]');
    if (!sectionElement) return false;

    sectionElement.innerHTML = sectionHtml;
    return true;
  }

  async syncFilledCartFromServer(existingSections = null) {
    let sectionRaw = existingSections?.['cart-drawer'];
    if (!sectionRaw) {
      const sections = await this.fetchFreshSections(['cart-drawer']);
      sectionRaw = sections?.['cart-drawer'];
    }

    if (!sectionRaw || !this.sectionHtmlHasCartItems(sectionRaw)) return false;

    const doc = new DOMParser().parseFromString(sectionRaw, 'text/html');
    const newCartItems = doc.querySelector('#CartDrawer-CartItems');
    const newFooter = doc.querySelector('#CartDrawer .drawer__footer');
    const newInventory = doc.querySelector('[data-beany-cart-inventory-message]');

    if (newCartItems) {
      const cartItemsEl = this.querySelector('#CartDrawer-CartItems');
      if (cartItemsEl) cartItemsEl.innerHTML = newCartItems.innerHTML;
    }

    if (newFooter) {
      const footerEl = this.querySelector('.drawer__footer');
      if (footerEl) footerEl.innerHTML = newFooter.innerHTML;
    }

    if (newInventory) {
      const inventoryEl = this.querySelector('[data-beany-cart-inventory-message]');
      if (inventoryEl) inventoryEl.innerHTML = newInventory.innerHTML;
    }

    this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    return true;
  }

  resolveItemCountFromCart(cart, fallback = 0) {
    if (cart?.item_count != null && cart.item_count !== '') {
      const count = Number(cart.item_count);
      if (!Number.isNaN(count)) return count;
    }
    if (Array.isArray(cart?.items) && cart.items.length > 0) {
      return cart.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    }
    return fallback;
  }

  isDrawerDomEmpty() {
    return this.countDomBagTotal() === 0 && !this.hasVisibleCartRows();
  }

  async confirmCartItemCount(changeResponse = null) {
    const fromChange = changeResponse
      ? this.resolveItemCountFromCart(changeResponse, -1)
      : -1;
    if (fromChange > 0) return fromChange;

    if (fromChange === 0 && this.isDrawerDomEmpty()) return 0;

    const cart = await this.fetchCartJson();
    const fromCart = cart ? this.resolveItemCountFromCart(cart, -1) : -1;

    if (fromChange === 0 && this.isDrawerDomEmpty()) return 0;
    if (fromCart > 0) return fromCart;

    const domQty = this.countDomBagTotal();
    const hasRows = this.hasVisibleCartRows();

    if (fromChange === 0 || fromCart === 0) {
      if (domQty > 0 || hasRows) return Math.max(domQty, hasRows ? 1 : 0);
      return 0;
    }

    if (domQty > 0) return domQty;
    if (hasRows) return 1;
    return 0;
  }

  refreshEmptyCartSection() {
    void this.fetchFreshSections(['cart-drawer']).then((sections) => {
      if (!sections?.['cart-drawer']) return;
      const html = this.getSectionInnerHTML(sections['cart-drawer'], '[data-beany-cart-drawer-dynamic]');
      this.replaceDynamicSection(html, 0);
    });
  }

  syncFilledCartInBackground(itemCount) {
    void (async () => {
      let synced = await this.syncFilledCartFromServer();
      if (!synced) {
        await new Promise((resolve) => window.setTimeout(resolve, 80));
        synced = await this.syncFilledCartFromServer();
      }
      if (synced && itemCount > 0) {
        window.BeanyCartDrawerPricing?.applyInventory?.(itemCount);
        window.BeanyCartDrawerPricing?.apply?.(itemCount);
      }
    })();
  }

  async applyCartAfterQuantityChange(changeResponse = null) {
    const fromChange = changeResponse
      ? this.resolveItemCountFromCart(changeResponse, -1)
      : -1;

    if (fromChange === 0 && this.isDrawerDomEmpty()) {
      this.showEmptyState();
      this.updateDrawerCount(0, { animate: false });
      window.BeanyCartDrawerPricing?.applyInventory?.(0);
      this.refreshEmptyCartSection();
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer' });
      }
      return;
    }

    const itemCount =
      fromChange > 0 ? fromChange : await this.confirmCartItemCount(changeResponse);

    if (itemCount > 0) {
      this.hideEmptyState();
      this.updateDrawerCount(itemCount, { animate: false });
      window.BeanyCartDrawerPricing?.applyInventory?.(itemCount);
      window.BeanyCartDrawerPricing?.apply?.(itemCount);

      this.enableCheckoutButtons();

      this.syncFilledCartInBackground(itemCount);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer' });
      }
      return;
    }

    this.showEmptyState();
    this.updateDrawerCount(0, { animate: false });
    window.BeanyCartDrawerPricing?.applyInventory?.(0);
    this.refreshEmptyCartSection();

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer' });
    }
  }

  showLoading(triggeredBy, itemCount = null) {
    const resolvedCount =
      typeof itemCount === 'number' ? itemCount : this.getResolvedBagCount();
    this.openFast(triggeredBy, resolvedCount);
  }

  openFast(triggeredBy, itemCount = 0, lineItems = []) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.classList.remove('is-loading');
    this.classList.add('is-loading-fast');
    this.hideEmptyState();
    this.updateDrawerCount(itemCount, { animate: itemCount > 0 });

    if (lineItems.length) {
      this.renderOptimisticLineItems(lineItems, itemCount);
    }

    if (itemCount > 0) this.enableCheckoutButtons();

    this.open(triggeredBy, { immediate: true });
    window.BeanyCartDrawerPricing?.applyInventory?.(itemCount);
    window.BeanyCartDrawerPricing?.apply?.(itemCount);
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  renderOptimisticLineItems(lineItems, totalQty = 0) {
    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;

    inner.querySelector('.drawer__inner-empty')?.setAttribute('hidden', 'hidden');

    const cartItemsEl = this.querySelector('cart-drawer-items');
    if (cartItemsEl) cartItemsEl.classList.remove('is-empty');

    const contents = this.querySelector('#CartDrawer-CartItems');
    if (!contents) return;

    const rows = lineItems
      .map((item, index) => {
        const toneRaw = String(item.tone || "original").toLowerCase();
        const tone =
          toneRaw.includes("vanilla") || toneRaw.includes("french")
            ? "vanilla"
            : toneRaw.includes("caramel") || toneRaw.includes("cookie")
              ? "caramel"
              : "original";
        return `
        <tr class="cart-item beany-cart-drawer__item--optimistic" id="CartDrawer-Item-${index + 1}">
          <td class="cart-item__media">
            <div class="beany-cart-drawer__flavor-media beany-cart-drawer__flavor-media--${tone}">
              <span class="beany-cart-drawer__flavor-tone" aria-hidden="true"></span>
              ${
                item.image
                  ? `<a class="cart-item__link" tabindex="-1" aria-hidden="true"> </a><img class="cart-item__image beany-cart-drawer__flavor-bag" src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.title)}" width="150" height="188" loading="eager">`
                  : ""
              }
            </div>
          </td>
          <td class="cart-item__details">
            <span class="cart-item__name h4 break">${this.escapeHtml(item.title)}</span>
            <p class="beany-cart-drawer__renew-note" data-beany-cart-renew-note dir="rtl" lang="he">
              <span data-beany-cart-renew-text>
                מתחדש כל
                <span data-beany-cart-renew-days dir="ltr">30</span>
                ימים
              </span>
            </p>
            <div class="beany-cart-drawer__price-row" dir="ltr">
              <s class="beany-cart-drawer__compare-price" data-beany-cart-compare-price hidden></s>
              <span class="beany-cart-drawer__unit-price" data-beany-cart-unit-price></span>
            </div>
          </td>
          <td class="cart-item__quantity beany-cart-drawer__quantity">
            <div class="beany-cart-drawer__quantity-wrap">
              <div class="quantity cart-quantity beany-cart-drawer-qty">
                <button class="quantity__button no-js-hidden" name="minus" type="button" aria-label="הפחת כמות">
                  <span class="beany-cart-drawer-qty__symbol" aria-hidden="true">−</span>
                </button>
                <input
                  class="quantity__input"
                  type="number"
                  value="${item.quantity}"
                  min="0"
                  data-index="${index + 1}"
                  data-quantity-variant-id="${item.variantId || ""}"
                  data-line-key="${item.lineKey || ""}"
                  readonly
                >
                <button class="quantity__button no-js-hidden" name="plus" type="button" aria-label="הוסף כמות">
                  <span class="beany-cart-drawer-qty__symbol" aria-hidden="true">+</span>
                </button>
              </div>
            </div>
          </td>
        </tr>`;
      })
      .join('');

    contents.innerHTML = `
      <div class="drawer__cart-items-wrapper beany-cart-drawer__optimistic">
        <table class="cart-items" role="table">
          <tbody role="rowgroup">${rows}</tbody>
        </table>
      </div>
      <p id="CartDrawer-LiveRegionText" class="visually-hidden" role="status"></p>
      <p id="CartDrawer-LineItemStatus" class="visually-hidden" aria-hidden="true" role="status"></p>
    `;
  }

  clearLoading() {
    this.classList.remove('is-loading', 'is-loading-fast');
    this.querySelector('[data-cart-drawer-loading]')?.remove();
    this.querySelector('[data-cart-drawer-loading-host]')?.removeAttribute('data-cart-drawer-loading-host');
  }

  prefetchSections() {
    if (this._prefetchPromise) return this._prefetchPromise;

    this._prefetchPromise = Promise.all([
      this.fetchFreshSections(),
      fetch(`${window.routes?.cart_url || '/cart'}.js`, { cache: 'no-store' })
        .then((response) => response.json())
        .catch(() => null),
    ])
      .then(([sections, cart]) => {
        this._prefetchedAt = Date.now();
        if (sections) this._prefetchedSections = sections;
        if (cart) this._prefetchedCart = cart;
        return sections;
      })
      .finally(() => {
        this._prefetchPromise = null;
      });

    return this._prefetchPromise;
  }

  consumePrefetchedSections(maxAgeMs = 45000) {
    if (!this._prefetchedSections || Date.now() - (this._prefetchedAt || 0) > maxAgeMs) {
      return null;
    }
    const sections = this._prefetchedSections;
    this._prefetchedSections = null;
    return sections;
  }

  consumePrefetchedCart(maxAgeMs = 45000) {
    if (!this._prefetchedCart || Date.now() - (this._prefetchedAt || 0) > maxAgeMs) {
      return null;
    }
    const cart = this._prefetchedCart;
    this._prefetchedCart = null;
    return cart;
  }

  static sectionsUrl(pathname = window.location.pathname) {
    const base = pathname || '/';
    const joiner = base.includes('?') ? '&' : '?';
    return `${base}${joiner}_=${Date.now()}`;
  }

  async fetchFreshSections(sectionIds = null) {
    const ids = sectionIds || this.getSectionsToRender().map((section) => section.id);
    const url = `/?sections=${ids.join(',')}&_=${Date.now()}`;
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) return null;
    return response.json();
  }

  async refreshFromCart(options = {}) {
    const shouldOpen = options.open !== false;
    const trigger = options.triggeredBy || null;

    if (shouldOpen) {
      if (trigger) this.setActiveElement(trigger);
      this.showLoading(trigger);
    }

    try {
      const cart = await this.fetchCartJson();
      const sections = await this.fetchFreshSections();

      if (!sections) {
        this.clearLoading();
        return;
      }

      await this.renderContents(
        {
          sections,
          item_count: cart?.item_count ?? 0,
          items: cart?.items ?? [],
        },
        { open: shouldOpen, cart }
      );
    } catch (error) {
      console.error(error);
      this.clearLoading();
    }
  }

  async renderContents(parsedState, options = {}) {
    const shouldOpen = options.open !== false;
    const previousCount = Number(this._displayCount ?? this.querySelector('[data-cart-drawer-count]')?.dataset.count ?? 0);

    this.clearLoading();
    this.productId = parsedState.id;

    const cart = options.cart || (await this.fetchCartJson());
    let itemCount = cart?.item_count ?? this.resolveItemCount(parsedState);
    const state = {
      ...parsedState,
      item_count: itemCount,
      items: cart?.items ?? parsedState.items ?? [],
    };

    let sections = state.sections || {};
    let sectionHtml = sections['cart-drawer']
      ? this.getSectionInnerHTML(sections['cart-drawer'], '[data-beany-cart-drawer-dynamic]')
      : '';

    if (itemCount > 0) {
      this.hideEmptyState();
      const synced = await this.syncFilledCartFromServer(sections);
      if (!synced) {
        if (!sectionHtml) {
          const freshSections = await this.fetchFreshSections(['cart-drawer']);
          if (freshSections?.['cart-drawer']) {
            sections = { ...sections, ...freshSections };
            sectionHtml = this.getSectionInnerHTML(
              freshSections['cart-drawer'],
              '[data-beany-cart-drawer-dynamic]'
            );
          }
        }
        if (sectionHtml) {
          this.replaceDynamicSection(sectionHtml, itemCount);
          this.hideEmptyState();
        }
      }
      this.updateDrawerCount(itemCount, { animate: itemCount > previousCount });
    } else {
      if (!sectionHtml) {
        const freshSections = await this.fetchFreshSections(['cart-drawer']);
        if (freshSections?.['cart-drawer']) {
          sectionHtml = this.getSectionInnerHTML(
            freshSections['cart-drawer'],
            '[data-beany-cart-drawer-dynamic]'
          );
        }
      }
      if (sectionHtml) {
        this.replaceDynamicSection(sectionHtml, 0);
      }
      this.showEmptyState();
      this.updateDrawerCount(0, { animate: false });
    }

    this.bindOverlayClose();
    this.bindCartIcon();

    requestAnimationFrame(() => {
      window.BeanyCartDrawerPricing?.applyInventory?.(itemCount);
      window.BeanyCartDrawerPricing?.apply?.(itemCount);
    });

    if (shouldOpen && !this.classList.contains('active')) {
      this.open();
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer' });
    }
  }

  getSectionInnerHTML(html, selector = '[data-beany-cart-drawer-dynamic]') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const target =
      (selector && doc.querySelector(`#CartDrawer ${selector}`)) ||
      (selector && doc.querySelector(selector)) ||
      doc.querySelector('#CartDrawer [data-beany-cart-drawer-dynamic]');
    return target ? target.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '[data-beany-cart-drawer-dynamic]'
      }
    ];
  }

  getSectionIdsForCartApi() {
    const ids = this.getSectionsToRender().map((section) => section.id);
    if (document.getElementById('cart-icon-bubble')) {
      ids.push('cart-icon-bubble');
    }
    return ids;
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  releaseCheckoutUi() {
    this.clearLoading();
    this.querySelectorAll('#CartDrawer-Checkout, #CartDrawer-Checkout-OneTime, .cart__checkout-button').forEach((btn) => {
      btn.classList.remove('loading');
      btn.removeAttribute('aria-busy');
      delete btn.dataset.beanySubscribing;
      const spinner = btn.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.add('hidden');
    });
    if (this.getResolvedBagCount() > 0) this.enableCheckoutButtons();
  }

  async restoreAfterHistory() {
    this.releaseCheckoutUi();
    const cart = await this.fetchCartJson();
    const count = this.resolveItemCountFromCart(cart, 0);

    if (count < 1) {
      this.showEmptyState();
      this.updateDrawerCount(0, { animate: false });
      window.BeanyCartDrawerPricing?.applyInventory?.(0);
      return;
    }

    this.hideEmptyState();
    this.updateDrawerCount(count, { animate: false });
    await this.syncFilledCartFromServer();
    this.releaseCheckoutUi();
    window.BeanyCartDrawerPricing?.applyInventory?.(count);
    window.BeanyCartDrawerPricing?.apply?.(count);
  }
}

customElements.define('cart-drawer', CartDrawer);

window.addEventListener('pagehide', () => {
  document.querySelector('cart-drawer')?.releaseCheckoutUi?.();
});

window.addEventListener('pageshow', (event) => {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  drawer.releaseCheckoutUi?.();
  if (event.persisted) drawer.restoreAfterHistory?.();
});

class CartDrawerItems extends CartItems {
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items' || event.source === 'cart-drawer') {
        return;
      }
      this.onCartUpdate();
    });
  }

  onChange(event) {
    if (this._beanyQtyHandled) return;
    if (event.target?.closest?.('.beany-cart-drawer-qty')) return;
    const line = event.target?.dataset?.index;
    if (line && this.updateDrawerLineQuantity) {
      this.updateDrawerLineQuantity(
        line,
        event.target.value,
        document.activeElement?.getAttribute('name'),
        event.target?.dataset?.lineKey || null
      );
      return;
    }
    super.onChange(event);
  }

  isCartChangeError(parsedState, ok) {
    if (!ok) return true;
    if (parsedState?.errors) return true;
    if (parsedState?.status === 'bad_request') return true;
    if (parsedState?.status === 422 || parsedState?.status === '422') return true;
    if (typeof parsedState?.status === 'number' && parsedState.status >= 400) return true;
    return false;
  }

  updateDrawerLineQuantity(line, quantity, name, lineKey = null) {
    const lineIndex = Number.parseInt(line, 10);
    if (!lineIndex && !lineKey) return;

    const input = lineKey
      ? this.querySelector(
          `[data-line-key="${typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(lineKey) : lineKey}"]`
        )
      : this.querySelector(`#Drawer-quantity-${lineIndex}`) ||
        this.querySelector(`[data-index="${lineIndex}"]`);

    if (lineIndex) this.enableLoading(lineIndex);

    const changePayload = { quantity: Number(quantity) };

    if (lineKey) {
      changePayload.id = String(lineKey);
    } else if (input?.dataset?.quantityVariantId) {
      changePayload.id = String(input.dataset.quantityVariantId);
    } else if (lineIndex) {
      changePayload.line = lineIndex;
    }

    const body = JSON.stringify(changePayload);
    const cartDrawer = document.querySelector('cart-drawer');
    const removedRow =
      Number(quantity) === 0
        ? input?.closest('.cart-item') ||
          (lineIndex ? document.getElementById(`CartDrawer-Item-${lineIndex}`) : null)
        : null;

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text().then((state) => ({ ok: response.ok, state })))
      .then(async ({ ok, state }) => {
        const parsedState = JSON.parse(state);

        if (this.isCartChangeError(parsedState, ok)) {
          removedRow?.classList.remove('beany-cart-drawer__item--removing');
          if (input) input.value = input.getAttribute('value') || input.defaultValue;
          if (lineIndex) {
            this.updateLiveRegions(
              lineIndex,
              parsedState.errors || parsedState.description || parsedState.message
            );
          }
          await cartDrawer?.refreshFromCart?.({ open: false });
          return;
        }

        if (removedRow?.isConnected) {
          removedRow.remove();
        }

        await cartDrawer?.applyCartAfterQuantityChange?.(parsedState);
      })
      .catch(() => {
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
        cartDrawer?.refreshFromCart?.({ open: false });
      })
      .finally(() => {
        if (lineIndex) this.disableLoading(lineIndex);
      });
  }

  updateQuantityByVariant(variantId, quantity, name) {
    const input = this.querySelector(`[data-quantity-variant-id="${variantId}"]`);
    if (!input) return;
    this.updateDrawerLineQuantity(
      input.dataset.index,
      quantity,
      name,
      input.dataset.lineKey || null
    );
  }

  onCartUpdate() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer?.refreshFromCart) return;
    cartDrawer.refreshFromCart({ open: false }).catch((error) => {
      console.error(error);
    });
  }

  getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const target =
      doc.querySelector(`#CartDrawer ${selector}`) || doc.querySelector(selector);
    return target ? target.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '[data-beany-cart-drawer-dynamic]'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function bindBeanyCartDrawerIcon() {
  document.querySelector('cart-drawer')?.bindCartIcon();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindBeanyCartDrawerIcon);
} else {
  bindBeanyCartDrawerIcon();
}

document.addEventListener('shopify:section:load', bindBeanyCartDrawerIcon);
